/**
 * ChatProvider — wraps the authenticated layout, owns chat state, and
 * pairs the main page with a push-style chat panel (issue #351,
 * subtask 09 refresh).
 *
 * The panel pushes the main column AND the layout's fixed header bar:
 * we publish ``--chat-panel-width`` on ``document.documentElement`` so
 * the design-system ``MainLayout`` header's ``right`` edge tracks the
 * panel width.
 *
 * Width is user-resizable via the left-edge drag handle; the chosen
 * width is persisted to ``localStorage`` so it survives reloads.
 *
 * Also exposes ``ChatContext`` so any route (notably ``/apps``) can
 * call ``openChat({ prefill, sourceApp })`` to pop the widget with a
 * seeded draft for the #945 "Feedback geben" flow.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChatLauncher } from './ChatLauncher';
import { ChatPanel } from './ChatPanel';
import { TechsupportTab } from './tabs/TechsupportTab';
import { KiAssistantTab } from './tabs/KiAssistantTab';
import {
  getChatTabs,
  registerChatTab,
  unregisterChatTab,
  type ChatTabDescriptor,
} from './ChatTabRegistry';
import { useChatSSE } from '../../hooks/useChatSSE';
import { useChatThreads } from '../../hooks/useChatQueries';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ChatContext, type ChatOpenOptions } from './ChatContext';

export interface ChatProviderProps {
  children: React.ReactNode;
}

const DEFAULT_PANEL_WIDTH = 480;
const MIN_PANEL_WIDTH = 320;
const PANEL_WIDTH_LS_KEY = 'hofsync.chat.panel-width';

function readPersistedWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_PANEL_WIDTH;
  try {
    const raw = window.localStorage.getItem(PANEL_WIDTH_LS_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    if (Number.isFinite(parsed) && parsed >= MIN_PANEL_WIDTH) return parsed;
  } catch {
    /* swallow storage errors (private mode, quota) */
  }
  return DEFAULT_PANEL_WIDTH;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  // Suppress the floating launcher when the admin already has the
  // full-page ``/admin/messages`` tool open — two chat surfaces on the
  // same screen would race for the same SSE stream and confuse the user.
  const launcherSuppressed = location.pathname.startsWith('/admin/messages');
  const [isOpen, setIsOpen] = useState(false);
  const [tabs, setTabs] = useState<ChatTabDescriptor[]>([]);
  const [panelWidth, setPanelWidth] = useState<number>(readPersistedWidth);
  const pendingPrefillRef = useRef<ChatOpenOptions | null>(null);
  const [prefillVersion, setPrefillVersion] = useState(0);

  useChatSSE({ enabled: true });

  // Drive the launcher badge from the thread-list query directly so the
  // count is correct even when the panel — and therefore TechsupportTab
  // — is unmounted. SSE invalidations refresh this query in the
  // background, so a new incoming message ticks the badge in near
  // real-time.
  const { data: userThreads } = useChatThreads();
  const launcherUnread = useMemo(
    () =>
      (userThreads ?? []).reduce(
        (acc, thread) => acc + (thread.unread_count ?? 0),
        0,
      ),
    [userThreads],
  );

  // Drive the Hofsync-Support tab badge from the SAME always-on query
  // rather than an in-tab counter (#1735). ``ChatPanel`` mounts only the
  // ACTIVE tab, so a ``TechsupportTab``-owned counter went stale whenever
  // the user sat on another tab (now the default, post-#1734) — a support
  // reply arriving in the background never lit the badge. Sourcing it from
  // the ``human_support`` thread's server-side ``unread_count`` keeps the
  // badge correct regardless of which tab is active or whether the panel
  // is even open; SSE invalidations tick it in near real-time.
  const techsupportUnread = useMemo(
    () =>
      (userThreads ?? []).find(
        (thread) => thread.thread_kind === 'human_support',
      )?.unread_count ?? 0,
    [userThreads],
  );

  // Publish panel width so the MainLayout header + the impersonation
  // banner shrink horizontally (CSS uses ``right: var(--chat-panel-width)``).
  // Also set ``right`` directly on the header + banner elements as an
  // imperative belt-and-suspenders pass — protects against any vite
  // HMR / cache lag where the var-driven rule hasn't recomputed for
  // an already-rendered fixed element.
  useEffect(() => {
    const root = document.documentElement;
    const target = `${isOpen ? panelWidth : 0}px`;
    root.style.setProperty('--chat-panel-width', target);
    const header = document.querySelector(
      '.main-layout__header',
    ) as HTMLElement | null;
    if (header) {
      // ``width: auto`` is the load-bearing line — the inner
      // ``.header`` molecule sets ``width: 100%``, which would
      // over-constrain ``left:0 + right:Npx`` and cause the spec
      // to ignore the right constraint entirely, leaving the
      // header full-width behind the chat panel.
      header.style.width = 'auto';
      header.style.right = target;
      header.style.transition = 'right 0.2s ease';
    }
    const banner = document.querySelector(
      '.impersonation-banner',
    ) as HTMLElement | null;
    if (banner) {
      banner.style.right = target;
      banner.style.transition = 'right 0.2s ease';
    }
    return () => {
      root.style.setProperty('--chat-panel-width', '0px');
      const h = document.querySelector(
        '.main-layout__header',
      ) as HTMLElement | null;
      if (h) {
        h.style.right = '0';
        h.style.width = '';
      }
      const b = document.querySelector(
        '.impersonation-banner',
      ) as HTMLElement | null;
      if (b) b.style.right = '0';
    };
  }, [isOpen, panelWidth]);

  const persistWidth = useCallback((next: number) => {
    try {
      window.localStorage.setItem(PANEL_WIDTH_LS_KEY, String(next));
    } catch {
      /* swallow storage errors */
    }
  }, []);

  const open = useCallback((options?: ChatOpenOptions) => {
    if (options && (options.prefill || options.sourceApp)) {
      pendingPrefillRef.current = options;
      setPrefillVersion((v) => v + 1);
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const consumePrefill = useCallback(() => {
    const current = pendingPrefillRef.current;
    pendingPrefillRef.current = null;
    return current;
  }, []);

  useEffect(() => {
    // Registration order is the sole source of left→right tab order and
    // the panel's default active tab (ChatPanel derives it from tabs[0]).
    // #1734: KI-Hofassistent leads and is the default-open tab.
    registerChatTab({
      id: 'ki-assistant',
      label: t('chat.tabs.kiAssistant', 'KI-Hofassistent'),
      render: () => <KiAssistantTab />,
    });
    registerChatTab({
      id: 'techsupport',
      label: t('chat.tabs.techsupport', 'Hofsync Support'),
      badgeCount: techsupportUnread,
      render: () => (
        <TechsupportTab isOpen={isOpen} prefillVersion={prefillVersion} />
      ),
    });
    setTabs(getChatTabs());
    return () => {
      unregisterChatTab('techsupport');
      unregisterChatTab('ki-assistant');
      setTabs(getChatTabs());
    };
  }, [t, techsupportUnread, isOpen, prefillVersion]);

  // FAB launcher badge: the sum across ALL threads from the always-on
  // query, so it stays correct while the panel is closed. The per-tab
  // badges now derive from this same query (#1735), so there is no longer
  // an independent in-tab counter to fall back to.
  const totalUnread = launcherUnread;

  const contextValue = useMemo(
    () => ({ isOpen, open, close, consumePrefill }),
    [isOpen, open, close, consumePrefill],
  );

  return (
    <ChatContext.Provider value={contextValue}>
      <div className="chat-host">
        <div className="chat-host__main">{children}</div>
        <ChatPanel
          isOpen={isOpen}
          onClose={close}
          tabs={tabs}
          width={panelWidth}
          onWidthChange={(next) => {
            setPanelWidth(next);
            persistWidth(next);
          }}
        />
      </div>
      {!isOpen && !launcherSuppressed && (
        <ChatLauncher
          isOpen={isOpen}
          unreadCount={totalUnread}
          onClick={() => setIsOpen(true)}
        />
      )}
    </ChatContext.Provider>
  );
};
